import html2pdf from 'html2pdf.js';
import type { LoadLetter } from '../types/loadLetter';

/**
 * Generates and downloads a PDF from a Load Letter's HTML content
 * @param loadLetter - The load letter object containing the HTML to convert
 * @returns Promise that resolves when PDF generation is complete
 */
export async function generateLoadLetterPdf(loadLetter: LoadLetter): Promise<void> {
  try {
    // Format staff name for filename (replace spaces with underscores, remove special chars)
    const staffNameFormatted = loadLetter.staffName
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '_');

    // Format semester for filename
    const semesterFormatted = loadLetter.semester === 'semester1'
      ? 'Semester1'
      : loadLetter.semester === 'semester2'
      ? 'Semester2'
      : 'Both';

    // Generate filename: LoadLetter_StaffName_Year_Semester.pdf
    const filename = `LoadLetter_${staffNameFormatted}_${loadLetter.academicYear}_${semesterFormatted}.pdf`;

    // Clone the HTML content
    const htmlContent = loadLetter.generatedHtml || '<p>No content available</p>';

    // Create a temporary container to manipulate the HTML
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlContent;

    // Remove explicit page break divs but keep the structure for page separation
    const pageBreakDivs = tempContainer.querySelectorAll('div[style*="page-break-after"], div[style*="break-after"]');
    pageBreakDivs.forEach(div => {
      // Only remove if it's just a page break div (no other content)
      if (div.textContent?.trim() === '') {
        div.remove();
      }
    });

    // Ensure page-2 has proper class for page break control
    const page2Elements = tempContainer.querySelectorAll('.page-2');
    page2Elements.forEach(element => {
      element.classList.add('force-new-page');
    });

    // Remove duplicate date in letter opening (if it exists right after the header date)
    const page1 = tempContainer.querySelector('.page-1');
    if (page1) {
      // Check all paragraphs in page-1 for duplicate dates
      const allParagraphs = page1.querySelectorAll('p');
      const datePattern = /^(\d{1,2}[\s\/-])?([A-Z][a-z]+|\d{1,2})[\s\/-]\d{1,4}(,?\s*\d{2,4})?$/;

      let foundHeaderDate = false;
      allParagraphs.forEach(p => {
        const text = p.textContent?.trim() || '';
        if (datePattern.test(text)) {
          if (foundHeaderDate) {
            // This is a duplicate date, remove it
            p.remove();
          } else {
            // This is the first (header) date, keep it
            foundHeaderDate = true;
          }
        }
      });
    }

    // Add CSS to ensure letter fits on page 1, data table on page 2
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      /* Ensure page 1 (letter) fits on one page */
      .page-1 {
        max-height: 95vh;
      }

      .page-1 * {
        font-size: 11px !important;
        line-height: 1.4 !important;
      }

      .page-1 h1, .page-1 h2, .page-1 h3 {
        font-size: 14px !important;
        margin: 8px 0 !important;
      }

      .page-1 p {
        margin: 6px 0 !important;
      }

      .page-1 ul, .page-1 ol {
        margin: 8px 0 !important;
        padding-left: 20px !important;
      }

      .page-1 li {
        margin-bottom: 4px !important;
      }

      /* Banner images should maintain their full size and aspect ratio */
      img[src="/pageheader.jpg"] {
        max-height: none !important;
        height: auto !important;
        width: 100% !important;
        max-width: 100% !important;
        object-fit: contain !important;
      }

      /* Other images (like logos) should be smaller */
      .page-1 img:not([src="/pageheader.jpg"]) {
        max-height: 60px !important;
      }

      .page-2 img:not([src="/pageheader.jpg"]) {
        max-height: none !important;
      }

      .page-1 .letter-opening,
      .page-1 .letter-body,
      .page-1 .letter-closing {
        margin-bottom: 12px !important;
      }

      /* Force page 2 to start on a new page */
      .page-2, .force-new-page {
        page-break-before: always !important;
        break-before: page !important;
      }

      /* Prevent awkward splits in data table */
      .page-2 table {
        page-break-inside: avoid !important;
      }

      .page-2 tr {
        page-break-inside: avoid !important;
      }

      .page-2 th, .page-2 td {
        page-break-inside: avoid !important;
      }

      /* Keep headers with content */
      h1, h2, h3, h4, h5, h6 {
        page-break-after: avoid !important;
      }
    `;
    tempContainer.insertBefore(styleElement, tempContainer.firstChild);

    // Configure html2pdf options
    const options = {
      margin: [10, 10, 10, 10] as [number, number, number, number], // Reduced margins for more content
      filename: filename,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        letterRendering: true,
        scrollY: 0,
        scrollX: 0
      },
      jsPDF: {
        unit: 'mm' as const,
        format: 'a4' as const,
        orientation: 'portrait' as const,
        compress: true
      },
      pagebreak: {
        mode: ['css', 'legacy'],
        before: ['.page-2', '.force-new-page'],
        avoid: ['tr', 'td', 'th', 'table']
      }
    };

    // Generate and download the PDF
    await html2pdf()
      .set(options)
      .from(tempContainer)
      .save();

  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
}
